import React, { useState, useEffect, useRef, useContext, useCallback, memo } from "react";
import { Row, Col, Card, Button } from "antd";
import { v4 as uuidv4 } from "uuid";
import { Widget, addResponseMessage, renderCustomComponent, toggleWidget, dropMessages, addLinkSnippet } from "react-chat-widget";
import axios from "axios";
import QuickReply from "../chat/quickreply";
import logo from "../../assets/chat/smile1.png";
import { validateADPassword } from "../chat/ADPasswordPolicy";
import { LanguageContext } from "../../context/LanguageContext";
import { pageConfig, getLoading } from "../configure/page";
import "./sgn.style.css";
import "../chat/styles.css";
import { useAuth } from "../../components/msalAuth/AuthProvider";
import { getText } from "../configure/utils";

const ChangeMode = memo(({ mode }) => (
  <div style={{ color: "#ccc" }}>
    <h6>Start Session {mode}</h6>
  </div>
));

const ADPasswordPolicy = memo(({ message }) => (
  <div className="rcw-message margin">
    <img src={logo} className="rcw-avatar" alt="profile" />
    <div className="rcw-response">
      <div className="rcw-message-text" dangerouslySetInnerHTML={{ __html: message }} />
    </div>
  </div>
));

const useApi = () => {
  const withTimeout = useCallback((promise, timeoutMs = 20000) => {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("Request timed out")), timeoutMs);

      promise
        .then((val) => {
          clearTimeout(timer);
          resolve(val);
        })
        .catch((err) => {
          clearTimeout(timer);
          reject(err);
        });
    });
  }, []);

  return { withTimeout };
};

const useChatElements = () => {
  const getChatElements = useCallback(
    () => ({
      sender: document.querySelector(".rcw-new-message"),
      button: document.querySelector(".rcw-send"),
      messagesContainer: document.querySelector(".rcw-messages-container"),
      senderContainer: document.querySelector(".rcw-sender"),
      launcher: document.querySelector(".rcw-launcher"),
    }),
    []
  );

  const applyDisabledStyle = useCallback(() => {
    const elements = getChatElements();
    if (!elements.sender) return;

    Object.values(elements).forEach((el) => {
      if (!el) return;
      el.style.backgroundColor = "#D4D7D9";
      if (el === elements.sender || el === elements.button) {
        el.setAttribute("disabled", "");
      }
    });
  }, [getChatElements]);

  const removeDisabledStyle = useCallback(() => {
    const elements = getChatElements();
    if (!elements.sender) return;

    Object.values(elements).forEach((el) => {
      if (!el) return;
      el.style.backgroundColor = "";
      if (el === elements.sender || el === elements.button) {
        el.removeAttribute("disabled");
      }
    });
  }, [getChatElements]);

  return { getChatElements, applyDisabledStyle, removeDisabledStyle };
};

export default function SignOnchat() {
  const { language } = useContext(LanguageContext);
  const { login } = useAuth();
  const { getSignOnChat } = pageConfig();
  const { withTimeout } = useApi();
  const { getChatElements, applyDisabledStyle, removeDisabledStyle } = useChatElements();

  const [sateshowflag, setSateshowflag] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  // const [isLoadingScreen, setIsLoadingScreen] = useState(false);

  const currentLanguageRef = useRef(language);
  const sessionRef = useRef("");
  const lambdaApiRef = useRef("");
  const csharpApiRef = useRef("");
  const typeofServiceRef = useRef("");
  const isBlockingUIRef = useRef(false);
  const isCloseSenderRef = useRef(false);
  const pasteEventNumRef = useRef(0);
  const launcherEventAddedRef = useRef(false);
  const isQuickReplyRef = useRef(false);
  const verifyADPassRef = useRef(false);
  const preventPasteRef = useRef(false);
  const isLoadingScreenRef = useRef(false);
  const errorMsg = useRef("");

  useEffect(() => {
    currentLanguageRef.current = language;
    errorMsg.current = getText("sandee.error");
  }, [language]);

  /**
   * Block the UI from user input if needed.
   */
  const blockUiEvent = useCallback(() => {
    setTimeout(() => {
      if (isBlockingUIRef.current) {
        applyDisabledStyle();
      } else if (isCloseSenderRef.current) {
        const { sender, button } = getChatElements();
        if (sender && button && !sender.hasAttribute("disabled") && !button.hasAttribute("disabled")) {
          sender.setAttribute("disabled", "");
          button.setAttribute("disabled", "");
        }
      }
    }, 500);
  }, [applyDisabledStyle, getChatElements]);

  /**
   * Unblock the UI from user input.
   */
  const unblockUiEvent = useCallback(() => {
    setTimeout(() => {
      removeDisabledStyle();
    }, 100);
  }, [removeDisabledStyle]);

  /**
   * Show error message and block UI.
   */
  const showErrorMessageAndBlockUI = useCallback(() => {
    // addResponseMessage(
    //   currentLanguageRef.current === "en"
    //     ? "Sorry, Sandee cannot continue. Please try again later."
    //     : "ขออภัยค่ะ น้องแสนดีไม่สามารถดำเนินการต่อได้ รบกวนทำรายการใหม่อีกครั้งนะคะ"
    // );
    addResponseMessage(errorMsg.current);
    isBlockingUIRef.current = true;
    blockUiEvent();
  }, [blockUiEvent]);

  /**
   * Toggle paste and drop actions on input field.
   */
  const togglePasteAndDrop = useCallback((disable) => {
    const inputField = document.querySelector(".rcw-new-message");
    if (!inputField) return;
    if (disable) {
      inputField.setAttribute("onpaste", "return false;");
      inputField.setAttribute("ondragover", "return false;");
      inputField.setAttribute("ondrop", "return false;");
    } else {
      inputField.removeAttribute("onpaste");
      inputField.removeAttribute("ondragover");
      inputField.removeAttribute("ondrop");
    }
  }, []);

  /**
   * Add launcher event once to handle widget open/close.
   */
  const addLauncherEvent = useCallback(() => {
    const { launcher } = getChatElements();
    if (launcher && !launcherEventAddedRef.current) {
      launcher.addEventListener("click", () => {
        const isWidgetClosed = document.getElementsByClassName("rcw-hide-sm").length === 0;
        if (isWidgetClosed) {
          if (isBlockingUIRef.current) {
            setTimeout(() => {
              applyDisabledStyle();
            }, 100);
          } else if (isCloseSenderRef.current) {
            setTimeout(() => {
              const { sender, button } = getChatElements();
              if (sender && button && !sender.hasAttribute("disabled") && !button.hasAttribute("disabled")) {
                sender.setAttribute("disabled", "");
                button.setAttribute("disabled", "");
              }
            }, 100);
          }

          if (preventPasteRef.current) {
            setTimeout(() => {
              togglePasteAndDrop(true);
            }, 500);
          }

          setTimeout(() => {
            if (isLoadingScreenRef.current) {
              const chatbox = document.querySelector(".rcw-messages-container");
              if (!chatbox) return;
              if (chatbox.style.position !== "relative") {
                chatbox.style.position = "relative";
              }
              chatbox.appendChild(getLoading());
              blockUiEvent();
            } else {
              const loadingOverlay = document.querySelector("#chat-loading-overlay");
              if (loadingOverlay) {
                loadingOverlay.remove();
              }
            }
          }, 200);
        }
      });
      launcherEventAddedRef.current = true;
    }
  }, [applyDisabledStyle, getChatElements, togglePasteAndDrop, blockUiEvent]);

  /**
   * Disable paste and drop event after a short delay if preventPaste is true.
   */
  const disablePasteAndDropEvent = useCallback(() => {
    setTimeout(() => {
      const { launcher } = getChatElements();
      if (launcher) {
        launcher.addEventListener("click", () => {
          if (preventPasteRef.current) {
            setTimeout(() => {
              togglePasteAndDrop(true);
            }, 500);
          }
        });
      }
    }, 500);
  }, [getChatElements, togglePasteAndDrop]);

  /**
   * Check if an intent requires the UI to be blocked.
   */
  const checkIntentForBlockUI = useCallback((intentName) => {
    const blockList = [
      "Report Problem Completed",
      "8.2 SurveyCompleted",
      "Survey",
      "Case Opened",
      "Default Fallback Intent",
      "Report Requested Completed",
      "Report Problem UnCompleted",
      "5.3 OTP Expired",
      "Error",
    ];
    return blockList.includes(intentName);
  }, []);

  /**
   * Greeting function - send "greeting" message to initiate conversation.
   */
  const greeting = useCallback(
    async (lambda_api, serviceType) => {
      setTimeout(async () => {
        let lang = currentLanguageRef.current === "th" ? "TH" : "ENG";
        const chatbox = document.querySelector(".rcw-messages-container");
        if (!chatbox) return;

        if (chatbox.style.position !== "relative") {
          chatbox.style.position = "relative";
        }

        chatbox.appendChild(getLoading());
        isCloseSenderRef.current = true;
        isLoadingScreenRef.current = true;
        blockUiEvent();

        const greetingPayload = {
          session: sessionRef.current,
          queryInput: {
            text: {
              text: "greeting",
              languageCode: "en-US",
              typeofservice: serviceType,
            },
          },
        };

        if (!lambda_api) {
          showErrorMessageAndBlockUI();
          return;
        }

        try {
          const response = await withTimeout(axios.post(lambda_api, greetingPayload));
          const messages = response.data?.queryResult?.fulfillmentMessages?.filter((item) => item.platform === "PLATFORM_UNSPECIFIED") || [];

          if (!messages.length) {
            showErrorMessageAndBlockUI();
          } else {
            isLoadingScreenRef.current = false;
            const loadingOverlay = document.querySelector("#chat-loading-overlay");
            if (loadingOverlay) {
              loadingOverlay.remove();
            }

            messages.forEach((msg) => {
              const fields = msg.payload.fields;
              if (fields.type.stringValue === "text" && fields.Lang.stringValue === lang) {
                addResponseMessage(fields.messages.stringValue);
              }
            });

            unblockUiEvent();
            isCloseSenderRef.current = false;
          }
        } catch (error) {
          const loadingOverlay = document.querySelector("#chat-loading-overlay");
          if (loadingOverlay) {
            loadingOverlay.remove();
          }
          console.error("Error in greeting:", error);
          showErrorMessageAndBlockUI();
        }
      }, 200);
    },
    [blockUiEvent, unblockUiEvent, showErrorMessageAndBlockUI, withTimeout]
  );

  /**
   * Parse and handle fulfillment messages from the server.
   */
  const handleFulfillmentMessage = useCallback((msg, lang) => {
    const { fields } = msg.payload;
    switch (fields.type.stringValue) {
      case "quick_replies":
        return {
          type: "quick_replies",
          title: fields.title.stringValue,
          buttons: fields.quick_replies.listValue.values.map((item) => item.stringValue),
        };
      case "text":
        if (fields.Lang.stringValue === lang) {
          return {
            type: "text",
            text: fields.messages.stringValue,
            validatePass: fields.mode?.stringValue === "validate-pass",
            disablePaste: fields.disablePaste?.stringValue,
          };
        }
        return null;
      case "link":
        return {
          type: "link",
          link: fields.messages.stringValue,
        };
      default:
        return null;
    }
  }, []);

  /**
   * Process fulfillment object and update the UI accordingly.
   */
  const processFulfillmentObject = useCallback(
    (object, newMessageHandler) => {
      switch (object.type) {
        case "quick_replies":
          if (!isQuickReplyRef.current) {
            isCloseSenderRef.current = true;
            blockUiEvent();
          }
          renderCustomComponent(
            QuickReply,
            {
              title: object.title,
              newMessage: newMessageHandler,
              buttons: object.buttons,
            },
            true
          );
          isQuickReplyRef.current = true;
          break;
        case "text":
          if (isCloseSenderRef.current && !isQuickReplyRef.current) {
            unblockUiEvent();
            isCloseSenderRef.current = false;
          }
          addResponseMessage(object.text);

          if (object.validatePass) verifyADPassRef.current = true;

          if (object.disablePaste === "true" && !preventPasteRef.current) {
            togglePasteAndDrop(true);
            preventPasteRef.current = true;
          } else if (object.disablePaste === "false" && preventPasteRef.current) {
            togglePasteAndDrop(false);
            preventPasteRef.current = false;
          }
          break;
        case "link":
          if (isCloseSenderRef.current && !isQuickReplyRef.current) {
            unblockUiEvent();
            isCloseSenderRef.current = false;
          }
          addLinkSnippet({
            title: "",
            link: object.link,
            target: "_blank",
          });
          break;
        default:
          break;
      }
    },
    [togglePasteAndDrop, unblockUiEvent, blockUiEvent]
  );

  /**
   * Handle new user messages, send to API, and process responses.
   */
  const handleNewUserMessage = useCallback(
    async (newMessage) => {
      let lang = currentLanguageRef.current === "th" ? "TH" : "ENG";
      isQuickReplyRef.current = false;

      if (verifyADPassRef.current) {
        const validationMessage = validateADPassword(newMessage.trim(), lang);
        if (validationMessage) {
          renderCustomComponent(ADPasswordPolicy, { message: validationMessage });
          addResponseMessage(lang === "ENG" ? "Please specify your password again." : "รบกวนระบุรหัสผ่านใหม่อีกครั้งค่ะ");
          return;
        }
        verifyADPassRef.current = false;
      }

      isCloseSenderRef.current = true;
      if (!verifyADPassRef.current) {
        blockUiEvent();
      }

      const userQuery = {
        session: sessionRef.current,
        queryInput: {
          text: {
            text: newMessage,
            languageCode: "en-US",
            typeofservice: typeofServiceRef.current,
          },
        },
      };

      try {
        let response = await withTimeout(axios.post(lambdaApiRef.current, userQuery));
        const action = response.data?.queryResult?.action;

        if (!response.data || !response.data.queryResult) {
          showErrorMessageAndBlockUI();
          return;
        }

        if (action && newMessage !== "Greeting" && action !== "input.unknown") {
          let csharpResponse;
          try {
            csharpResponse = await withTimeout(axios.post(csharpApiRef.current, response.data));
          } catch (err) {
            showErrorMessageAndBlockUI();
            return;
          }

          const followupEventInput = csharpResponse.data.jobject ? csharpResponse.data.jobject.followupEventInput : csharpResponse.data.data.followupEventInput;

          const eventPayload = {
            session: sessionRef.current,
            queryInput: {
              event: followupEventInput,
            },
          };

          try {
            response = await withTimeout(axios.post(lambdaApiRef.current, eventPayload));
          } catch (err) {
            showErrorMessageAndBlockUI();
            return;
          }

          if (!response.data || !response.data.queryResult) {
            showErrorMessageAndBlockUI();
            return;
          }

          const queryResult = response.data.queryResult;
          const messages = queryResult.fulfillmentMessages?.filter((item) => item.platform === "PLATFORM_UNSPECIFIED") || [];

          if (!messages.length) {
            showErrorMessageAndBlockUI();
            return;
          }

          messages.forEach((msg) => {
            const fulfillmentObj = handleFulfillmentMessage(msg, lang);
            if (fulfillmentObj) processFulfillmentObject(fulfillmentObj, handleNewUserMessage);
          });

          if (checkIntentForBlockUI(queryResult.intent?.displayName)) {
            isBlockingUIRef.current = true;
            isCloseSenderRef.current = false;
            blockUiEvent();
          }
        } else {
          const queryResult = response.data.queryResult;
          const messages = queryResult.fulfillmentMessages?.filter((item) => item.platform === "PLATFORM_UNSPECIFIED") || [];

          if (!messages.length) {
            showErrorMessageAndBlockUI();
            return;
          }

          messages.forEach((msg) => {
            const fulfillmentObj = handleFulfillmentMessage(msg, lang);
            if (fulfillmentObj) processFulfillmentObject(fulfillmentObj, handleNewUserMessage);
          });

          if (checkIntentForBlockUI(queryResult.intent?.displayName)) {
            isBlockingUIRef.current = true;
            isCloseSenderRef.current = false;
            blockUiEvent();
          }
        }
      } catch (error) {
        // Timeout or request error
        console.error("Error handling new user message:", error);
        showErrorMessageAndBlockUI();
      }
    },
    [blockUiEvent, checkIntentForBlockUI, handleFulfillmentMessage, processFulfillmentObject, showErrorMessageAndBlockUI, withTimeout]
  );

  /**
   * Initialize chat mode by resetting session, dropping messages, and greeting.
   */
  const initializeChatMode = useCallback(
    async (mode, lambdaUrl, csharpUrl, serviceType) => {
      if (isBlockingUIRef.current) {
        unblockUiEvent();
      }

      isBlockingUIRef.current = false;
      isCloseSenderRef.current = false;
      isLoadingScreenRef.current = false;
      if (preventPasteRef.current) {
        togglePasteAndDrop(false);
        preventPasteRef.current = false;
      }

      if (!sateshowflag) setSateshowflag(true);

      dropMessages();
      sessionRef.current = uuidv4();
      renderCustomComponent(ChangeMode, { mode }, false);

      lambdaApiRef.current = lambdaUrl;
      csharpApiRef.current = csharpUrl;
      typeofServiceRef.current = serviceType;

      await greeting(lambdaApiRef.current, typeofServiceRef.current);

      const isChatWidgetClosed = document.getElementsByClassName("rcw-opened").length === 0;
      if (isChatWidgetClosed) {
        toggleWidget();
      }

      addLauncherEvent();
    },
    [unblockUiEvent, greeting, addLauncherEvent, sateshowflag, togglePasteAndDrop]
  );

  /**
   * Handlers for menu clicks.
   */
  const menuADClick = useCallback(() => {
    verifyADPassRef.current = false;
    initializeChatMode("AD", process.env.REACT_APP_LAMBDA_AD, process.env.REACT_APP_CSHARP_API_AD, "AD");
    if (pasteEventNumRef.current === 0) {
      disablePasteAndDropEvent();
      pasteEventNumRef.current++;
    }
  }, [disablePasteAndDropEvent, initializeChatMode]);

  const menuSCGARIBAClick = useCallback(() => {
    initializeChatMode("SANDEE : SAP Ariba Sourcing", process.env.REACT_APP_LAMBDA_SCGARIBA, process.env.REACT_APP_CSHARP_API_SCGARIBA, "SCGP");
  }, [initializeChatMode]);

  const handleLogin = () => {
    login();
  };

  const [hoveredButton, setHoveredButton] = useState(null);

  return (
    <div className="sgn-menu-section" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <Card className={`sgn-menu-card ${isHovered ? "sgn-card-hovered" : ""}`}>
        <div className="sandee-text-head-title">
          <span>SANDEE</span>
        </div>
        <Row gutter={[16, 32]} className="sgn-row-chat">
          {getSignOnChat(language).map((chatItem, index) => {
            const isFirstButton = index === 0;
            const onClickHandler =
              chatItem.onClick === "handleLogin"
                ? handleLogin
                : chatItem.onClick === "menuADClick"
                ? menuADClick
                : chatItem.onClick === "menuSCGARIBAClick"
                ? menuSCGARIBAClick
                : null;

            return (
              <Col
                key={index}
                xs={isFirstButton ? 24 : 12}
                sm={isFirstButton ? 24 : 12}
                md={isFirstButton ? 24 : 12}
                lg={isFirstButton ? 24 : 12}
                xl={isFirstButton ? 24 : 12}
              >
                <div
                  className={`sgn-button-wrapper ${isFirstButton ? "" : "same-size-wrapper"}`}
                  style={{ display: "flex", alignItems: "center" }}
                  onMouseEnter={() => setHoveredButton(index)}
                  onMouseLeave={() => setHoveredButton(null)}
                >
                  <Button
                    type="primary"
                    onClick={onClickHandler}
                    className={`sgn-chat-button ${isFirstButton ? "sgn-chat-button-first" : ""} ${chatItem.buttonClass}`}
                    icon={
                      isFirstButton ? (
                        <img
                          src={hoveredButton === index ? chatItem.hoverImageSrc : chatItem.imageSrc}
                          alt={`icon ${chatItem.buttonClass}`}
                          className="sgn-button-icon"
                        />
                      ) : null
                    }
                    value={chatItem.buttonValue}
                  >
                    {isFirstButton ? (
                      <span>{chatItem.buttonText}</span>
                    ) : (
                      <div
                        className="other-button"
                        style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", textAlign: "center" }}
                      >
                        <img
                          src={hoveredButton === index ? chatItem.hoverImageSrc : chatItem.imageSrc}
                          alt={`icon ${chatItem.buttonClass}`}
                          className="sgn-button-icon"
                          style={{ marginBottom: "8px" }}
                        />
                        <span>{chatItem.buttonText}</span>
                      </div>
                    )}
                  </Button>
                </div>
              </Col>
            );
          })}
        </Row>
      </Card>
      {sateshowflag && (
        <Widget
          handleNewUserMessage={handleNewUserMessage}
          profileAvatar={logo}
          title="SANDEE Chatbot"
          subtitle=""
          senderPlaceHolder={getText("placeholdertext")}
          // fullScreenMode="true"
        />
      )}
    </div>
  );
}
